import { PureComponent, ReactNode } from 'react'
import cn from 'clsx'

// Components
import { MiniSong } from 'kit'

// Types
import type { LicenseSelectorInfo } from 'selectors'
import type { UserSong, Song } from 'types'

// Utils
import { priceString } from 'components/base/price'

import styles from './summary.css'

type LicenseSummaryProps = {
  userSong: UserSong
  song: Song
  licensee: string
  licenseInfo: LicenseSelectorInfo
  currency: string
  licensePrice: number
  vatPrice: number
  vatRate: number
  totalPrice: number
}

export const LicenseSummary = (props: LicenseSummaryProps): JSX.Element => {
  const { userSong, song, licensee, currency, licenseInfo, licensePrice, vatPrice, totalPrice, vatRate } = props
  const infoItems = [
    { label: 'Licensee:', value: licensee || '-' },
    { label: 'New song title:', value: userSong.song_title },
    {
      label: 'Time sampled from original song:',
      value: licenseInfo.descriptionShort,
    },
    {
      label: 'Sampled parts:',
      value: userSong.samples[0].sample_runtimes.map((runtime, index) => (
        <p style={{ marginBottom: 0 }} key={String(index)}>
          {runtime.start_time} to {runtime.end_time}: {runtime.description}
        </p>
      )),
    },
  ]
  const subItems = [
    {
      label: 'Sample license fee:',
      value: priceString(licensePrice, currency),
    },
    { label: `${vatRate}% VAT:`, value: priceString(vatPrice, currency) },
  ]

  return <Summary songs={[song]} infoItems={infoItems} subItems={subItems} total={priceString(totalPrice, currency)} /> // eslint-disable-line
}

type SummaryProps = {
  songs?: Array<Song>
  infoItems?: Array<{
    label: string
    value: string | ReactNode
  }>
  subItems: Array<{
    label: string
    value: string
  }>
  total: string
  listComponent?: ReactNode
  title?: string
}

class Summary extends PureComponent<SummaryProps> {
  static License: typeof LicenseSummary

  render() {
    const { songs, infoItems, subItems, total, listComponent, title } = this.props
    return (
      <div className={styles.summary}>
        <h3 className={styles.title}>{title || 'Summary'}</h3>
        {listComponent || (
          <ul className={styles.list}>
            {songs &&
              songs.map(song => (
                <li className={styles.infoItem} key={song.id}>
                  <MiniSong song={song} />
                </li>
              ))}
            <div className={styles.pad} key="pad" />
            {infoItems?.map(({ label, value }) => (
              <li className={styles.infoItem} key={label}>
                <span className={styles.label}>{label}</span>
                <span className={styles.desc}>{value}</span>
              </li>
            ))}
          </ul>
        )}
        <ul className={cn(styles.list, styles.subItemList)}>
          {subItems.map(({ label, value }) => (
            <li className={styles.subItem} key={label}>
              <span>{label}</span>
              <span>{value}</span>
            </li>
          ))}
        </ul>
        <span className={styles.total}>
          <span>Total:</span>
          <span>{total}</span>
        </span>
      </div>
    )
  }
}

Summary.License = LicenseSummary

export default Summary
